<script setup lang="ts">
/**
 * A KPI's health as a traffic light (BG §18.4, KPI health; docs/performance.md): three lamps in a housing
 * — red on top, amber in the middle, green at the bottom — with one lit. It reuses three status hues and
 * adds none: magenta is the red, because off target is an issue. It is always the housing, never a bare
 * dot, so a KPI's amber cannot be read as a run in flight.
 *
 * Colour is not the only channel: the lit lamp's position carries the state in greyscale, and the word
 * is its accessible name (`label`, or KPI_WORD). The lit lamp blinks on the status clock, holds still for
 * reduced motion, and pauses inside anything marked `data-loop="off"`. Size it with `--lamp`.
 */
import { KPI_WORD, type KpiRag } from '../lib/surfaces'

defineProps<{ rag: KpiRag; label?: string }>()
</script>

<template>
    <span class="zh-kpi-light" :data-rag="rag" role="img" :aria-label="label ?? KPI_WORD[rag]">
        <i class="zh-kpi-light__lamp" data-lamp="red" />
        <i class="zh-kpi-light__lamp" data-lamp="amber" />
        <i class="zh-kpi-light__lamp" data-lamp="green" />
    </span>
</template>

<style scoped>
.zh-kpi-light {
    --lamp: 0.375rem;
    display: inline-flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: 2px;
    padding: 3px;
    border: 1px solid var(--line);
    border-radius: 3px;
    background: var(--void-black);
}

.zh-kpi-light__lamp {
    display: block;
    width: var(--lamp);
    height: var(--lamp);
    border-radius: 50%;
    background: color-mix(in srgb, var(--hue) 16%, var(--void-black));
}

.zh-kpi-light__lamp[data-lamp="red"] {
    --hue: var(--state-failed);
}

.zh-kpi-light__lamp[data-lamp="amber"] {
    --hue: var(--state-active);
}

.zh-kpi-light__lamp[data-lamp="green"] {
    --hue: var(--state-complete);
}

.zh-kpi-light[data-rag="red"] .zh-kpi-light__lamp[data-lamp="red"],
.zh-kpi-light[data-rag="amber"] .zh-kpi-light__lamp[data-lamp="amber"],
.zh-kpi-light[data-rag="green"] .zh-kpi-light__lamp[data-lamp="green"] {
    background: var(--hue);
    box-shadow: 0 0 6px color-mix(in srgb, var(--hue) 70%, transparent);
}

/* BG §22.2: the status blink, on the shared 2.2s clock. Opacity only, and only the lit lamp. */
@media (prefers-reduced-motion: no-preference) {
    .zh-kpi-light[data-rag="red"] .zh-kpi-light__lamp[data-lamp="red"],
    .zh-kpi-light[data-rag="amber"] .zh-kpi-light__lamp[data-lamp="amber"],
    .zh-kpi-light[data-rag="green"] .zh-kpi-light__lamp[data-lamp="green"] {
        animation: lamp-blink var(--motion-status-blink) ease-in-out infinite;
    }

    [data-loop="off"] .zh-kpi-light__lamp {
        animation-play-state: paused;
    }
}

@keyframes lamp-blink {
    50% {
        opacity: 0.35;
    }
}
</style>
